import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Calendar, Users, BarChart3, MessageSquare, FileText,
  CheckCircle, ArrowRight, TrendingUp, Zap, Shield,
  Wrench, ShieldCheck, CarFront, Package, Boxes, BellRing, Landmark, Bot,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { BsInstagram, BsFacebook, BsLinkedin, BsYoutube, BsTwitterX, BsWhatsapp } from 'react-icons/bs';
import BrandLogo from '../../components/BrandLogo/BrandLogo';
import { useWebsiteTheme } from './useWebsiteTheme';
import ThemeToggle from './components/ThemeToggle';
import { USE_CASES } from '../../data/useCasesData';
import { websiteTokens, NAVY } from './websiteTokens';
import DeviceCollage from './components/DeviceCollage';
import ModuleShowcase, { EnterpriseBadge } from './components/ModuleShowcase';

// ── Inline SVG Illustrations ──────────────────────────────────────────────────

const HealthcareSVG = ({ c }) => (
  <svg width="220" height="200" viewBox="0 0 220 200" fill="none">
    {/* Building */}
    <rect x="50" y="90" width="100" height="95" rx="4" stroke={c} strokeWidth="2.5" />
    {/* Medical cross */}
    <rect x="88" y="106" width="24" height="8" rx="2" fill={c} />
    <rect x="98" y="96" width="8" height="28" rx="2" fill={c} />
    {/* Door */}
    <rect x="83" y="150" width="30" height="35" rx="3" stroke={c} strokeWidth="2" />
    {/* Windows */}
    <rect x="60" y="110" width="16" height="14" rx="2" stroke={c} strokeWidth="1.5" opacity=".5" />
    <rect x="140" y="110" width="16" height="14" rx="2" stroke={c} strokeWidth="1.5" opacity=".5" />
    {/* Calendar */}
    <rect x="130" y="20" width="68" height="60" rx="6" stroke={c} strokeWidth="2" />
    <line x1="130" y1="36" x2="198" y2="36" stroke={c} strokeWidth="1.5" />
    <rect x="140" y="43" width="9" height="9" rx="1.5" fill={c} opacity=".7" />
    <rect x="155" y="43" width="9" height="9" rx="1.5" fill={c} opacity=".7" />
    <rect x="170" y="43" width="9" height="9" rx="1.5" fill={c} opacity=".35" />
    <rect x="140" y="57" width="9" height="9" rx="1.5" fill={c} opacity=".35" />
    <rect x="155" y="57" width="9" height="9" rx="1.5" fill={c} opacity=".7" />
    <rect x="170" y="57" width="9" height="9" rx="1.5" fill={c} opacity=".35" />
    {/* Person */}
    <circle cx="22" cy="60" r="11" stroke={c} strokeWidth="2" />
    <path d="M6 95 C6 78 38 78 38 95" stroke={c} strokeWidth="2" />
    {/* Stethoscope arc */}
    <path d="M55 175 Q90 165 100 148" stroke={c} strokeWidth="2" strokeDasharray="5 3" />
    <circle cx="102" cy="146" r="4" fill={c} opacity=".6" />
  </svg>
);

const HomeMaintenanceSVG = ({ c }) => (
  <svg width="220" height="200" viewBox="0 0 220 200" fill="none">
    {/* House */}
    <path d="M60 110 L110 68 L160 110" stroke={c} strokeWidth="2.5" strokeLinejoin="round" />
    <rect x="68" y="110" width="84" height="70" rx="3" stroke={c} strokeWidth="2.5" />
    {/* Door */}
    <rect x="90" y="145" width="28" height="35" rx="3" stroke={c} strokeWidth="2" />
    {/* Window */}
    <rect x="112" y="120" width="20" height="18" rx="2" stroke={c} strokeWidth="1.5" opacity=".5" />
    {/* Mop / broom */}
    <line x1="30" y1="60" x2="55" y2="160" stroke={c} strokeWidth="2.5" strokeLinecap="round" />
    <path d="M22 64 L38 56" stroke={c} strokeWidth="2.5" strokeLinecap="round" />
    {/* Bucket */}
    <path d="M170 140 L178 180 H196 L204 140 Z" stroke={c} strokeWidth="2" fill="none" />
    <path d="M176 140 Q187 130 198 140" stroke={c} strokeWidth="2" fill="none" />
    {/* Water drops */}
    <path d="M185 100 Q185 90 192 100 Q185 108 185 100 Z" fill={c} opacity=".6" />
    <path d="M196 112 Q196 104 202 112 Q196 119 196 112 Z" fill={c} opacity=".4" />
    {/* Pest (bug) outline */}
    <ellipse cx="50" cy="185" rx="10" ry="7" stroke={c} strokeWidth="1.5" opacity=".5" />
    <line x1="42" y1="180" x2="35" y2="173" stroke={c} strokeWidth="1.5" opacity=".5" />
    <line x1="42" y1="185" x2="33" y2="185" stroke={c} strokeWidth="1.5" opacity=".5" />
    <line x1="58" y1="180" x2="65" y2="173" stroke={c} strokeWidth="1.5" opacity=".5" />
    <line x1="58" y1="185" x2="67" y2="185" stroke={c} strokeWidth="1.5" opacity=".5" />
  </svg>
);

const HomeImprovementsSVG = ({ c }) => (
  <svg width="220" height="200" viewBox="0 0 220 200" fill="none">
    {/* House */}
    <path d="M65 108 L115 62 L165 108" stroke={c} strokeWidth="2.5" strokeLinejoin="round" />
    <rect x="73" y="108" width="84" height="72" rx="3" stroke={c} strokeWidth="2.5" />
    {/* Door */}
    <rect x="96" y="148" width="28" height="32" rx="3" stroke={c} strokeWidth="2" />
    <circle cx="122" cy="165" r="2.5" fill={c} opacity=".5" />
    {/* Tree / plant */}
    <line x1="35" y1="180" x2="35" y2="130" stroke={c} strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="35" cy="112" r="22" stroke={c} strokeWidth="2" fill="none" />
    <circle cx="22" cy="122" r="12" stroke={c} strokeWidth="1.5" fill="none" opacity=".6" />
    <circle cx="48" cy="122" r="12" stroke={c} strokeWidth="1.5" fill="none" opacity=".6" />
    {/* Paint roller */}
    <rect x="168" y="60" width="32" height="20" rx="4" stroke={c} strokeWidth="2" />
    <line x1="184" y1="80" x2="184" y2="110" stroke={c} strokeWidth="2.5" strokeLinecap="round" />
    <line x1="175" y1="110" x2="195" y2="110" stroke={c} strokeWidth="2.5" strokeLinecap="round" />
    {/* Paint strokes */}
    <line x1="168" y1="90" x2="200" y2="90" stroke={c} strokeWidth="4" strokeLinecap="round" opacity=".3" />
    <line x1="168" y1="98" x2="200" y2="98" stroke={c} strokeWidth="4" strokeLinecap="round" opacity=".2" />
    {/* Stars */}
    <circle cx="200" cy="30" r="3" fill={c} opacity=".5" />
    <circle cx="185" cy="18" r="2" fill={c} opacity=".35" />
    <circle cx="210" cy="45" r="2" fill={c} opacity=".4" />
  </svg>
);

const BuildingServicesSVG = ({ c }) => (
  <svg width="220" height="200" viewBox="0 0 220 200" fill="none">
    {/* Multi-storey building */}
    <rect x="60" y="40" width="100" height="145" rx="4" stroke={c} strokeWidth="2.5" />
    {/* Windows grid */}
    {[55, 80, 105, 130].map(y =>
      [75, 100, 125].map(x => (
        <rect key={`${x}-${y}`} x={x} y={y} width="16" height="14" rx="2" stroke={c} strokeWidth="1.5" opacity=".5" />
      ))
    )}
    {/* Ground floor door */}
    <rect x="92" y="155" width="26" height="30" rx="3" stroke={c} strokeWidth="2" />
    {/* Window cleaner person */}
    <circle cx="30" cy="70" r="9" stroke={c} strokeWidth="2" />
    <path d="M22 88 L38 88 L38 120 L22 120 Z" stroke={c} strokeWidth="2" fill="none" rx="2" />
    {/* Rope */}
    <line x1="30" y1="58" x2="30" y2="26" stroke={c} strokeWidth="1.5" strokeDasharray="4 3" />
    {/* Squeegee */}
    <rect x="38" y="96" width="18" height="6" rx="2" fill={c} opacity=".7" />
    <line x1="56" y1="99" x2="64" y2="94" stroke={c} strokeWidth="2" strokeLinecap="round" />
    {/* AC unit */}
    <rect x="172" y="80" width="36" height="28" rx="4" stroke={c} strokeWidth="2" />
    <line x1="178" y1="90" x2="202" y2="90" stroke={c} strokeWidth="1.5" opacity=".5" />
    <line x1="178" y1="97" x2="202" y2="97" stroke={c} strokeWidth="1.5" opacity=".5" />
    {/* AC airflow */}
    <path d="M172 78 Q164 70 168 60" stroke={c} strokeWidth="1.5" strokeDasharray="3 3" opacity=".5" />
    <path d="M190 76 Q186 66 190 56" stroke={c} strokeWidth="1.5" strokeDasharray="3 3" opacity=".5" />
  </svg>
);

const FacilityServicesSVG = ({ c }) => (
  <svg width="220" height="200" viewBox="0 0 220 200" fill="none">
    {/* Toolbox */}
    <rect x="60" y="110" width="90" height="60" rx="5" stroke={c} strokeWidth="2.5" />
    <path d="M85 110 L85 98 Q110 90 135 98 L135 110" stroke={c} strokeWidth="2.5" fill="none" />
    <line x1="60" y1="128" x2="150" y2="128" stroke={c} strokeWidth="2" opacity=".5" />
    {/* Wrench */}
    <path d="M30 60 L70 100" stroke={c} strokeWidth="4" strokeLinecap="round" />
    <circle cx="28" cy="55" r="12" stroke={c} strokeWidth="2.5" fill="none" />
    <path d="M20 49 L36 61" stroke={c} strokeWidth="2" strokeLinecap="round" opacity=".5" />
    {/* Pipe */}
    <rect x="155" y="50" width="20" height="80" rx="4" stroke={c} strokeWidth="2" />
    <rect x="145" y="58" width="40" height="14" rx="4" stroke={c} strokeWidth="2" fill="none" />
    <rect x="145" y="112" width="40" height="14" rx="4" stroke={c} strokeWidth="2" fill="none" />
    {/* Water drop */}
    <path d="M165 148 Q165 138 172 148 Q165 157 165 148 Z" fill={c} opacity=".6" />
    <path d="M170 162 Q170 154 176 162 Q170 169 170 162 Z" fill={c} opacity=".4" />
    {/* Brickwork accent */}
    <rect x="70" y="155" width="22" height="10" rx="1" stroke={c} strokeWidth="1.2" opacity=".35" />
    <rect x="96" y="155" width="22" height="10" rx="1" stroke={c} strokeWidth="1.2" opacity=".35" />
    <rect x="82" y="165" width="22" height="10" rx="1" stroke={c} strokeWidth="1.2" opacity=".25" />
  </svg>
);

const PersonalCareSVG = ({ c }) => (
  <svg width="220" height="200" viewBox="0 0 220 200" fill="none">
    {/* Mirror */}
    <ellipse cx="110" cy="90" rx="45" ry="60" stroke={c} strokeWidth="2.5" />
    <rect x="100" y="150" width="20" height="30" rx="3" stroke={c} strokeWidth="2" />
    <line x1="88" y1="180" x2="132" y2="180" stroke={c} strokeWidth="2.5" strokeLinecap="round" />
    {/* Mirror reflection shine */}
    <path d="M80 65 Q88 58 96 66" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity=".4" />
    {/* Scissors */}
    <line x1="24" y1="40" x2="56" y2="90" stroke={c} strokeWidth="2.5" strokeLinecap="round" />
    <line x1="56" y1="40" x2="24" y2="90" stroke={c} strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="24" cy="40" r="8" stroke={c} strokeWidth="2" fill="none" />
    <circle cx="56" cy="40" r="8" stroke={c} strokeWidth="2" fill="none" />
    <circle cx="40" cy="65" r="4" fill="white" stroke={c} strokeWidth="1.5" />
    {/* Hair dryer */}
    <path d="M168 75 Q185 60 198 80 Q185 100 168 85 Z" stroke={c} strokeWidth="2" fill="none" />
    <line x1="168" y1="80" x2="150" y2="80" stroke={c} strokeWidth="3" strokeLinecap="round" />
    <path d="M162 92 L155 108" stroke={c} strokeWidth="2.5" strokeLinecap="round" />
    {/* Air lines */}
    <path d="M200 72 Q210 68 205 62" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity=".4" />
    <path d="M202 80 Q214 80 210 73" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity=".4" />
    {/* Sparkle stars */}
    <circle cx="150" cy="30" r="3" fill={c} opacity=".5" />
    <circle cx="165" cy="18" r="2" fill={c} opacity=".4" />
    <circle cx="140" cy="20" r="2" fill={c} opacity=".35" />
  </svg>
);

const AutoServicesSVG = ({ c }) => (
  <svg width="220" height="200" viewBox="0 0 220 200" fill="none">
    {/* Car body */}
    <path d="M30 120 L50 90 Q80 70 120 70 Q160 70 180 90 L200 120 L200 145 L30 145 Z" stroke={c} strokeWidth="2.5" fill="none" />
    {/* Windows */}
    <path d="M55 90 L70 76 Q95 72 115 72 L150 72 L170 90 Z" stroke={c} strokeWidth="1.5" fill="none" opacity=".5" />
    {/* Wheels */}
    <circle cx="72" cy="148" r="20" stroke={c} strokeWidth="2.5" />
    <circle cx="72" cy="148" r="9" stroke={c} strokeWidth="1.5" opacity=".5" />
    <circle cx="160" cy="148" r="20" stroke={c} strokeWidth="2.5" />
    <circle cx="160" cy="148" r="9" stroke={c} strokeWidth="1.5" opacity=".5" />
    {/* Headlight */}
    <ellipse cx="196" cy="115" rx="5" ry="8" stroke={c} strokeWidth="1.5" opacity=".6" />
    {/* Wrench */}
    <path d="M14 40 L42 68" stroke={c} strokeWidth="4" strokeLinecap="round" />
    <circle cx="12" cy="36" r="10" stroke={c} strokeWidth="2" fill="none" />
    <path d="M5 30 L19 42" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity=".5" />
    {/* Gear */}
    <circle cx="188" cy="44" r="16" stroke={c} strokeWidth="2" />
    <circle cx="188" cy="44" r="7" stroke={c} strokeWidth="2" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => {
      const r = Math.PI * deg / 180;
      const x1 = 188 + 16 * Math.cos(r);
      const y1 = 44 + 16 * Math.sin(r);
      const x2 = 188 + 22 * Math.cos(r);
      const y2 = 44 + 22 * Math.sin(r);
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={c} strokeWidth="3.5" strokeLinecap="round" />;
    })}
  </svg>
);

const EducationServicesSVG = ({ c }) => (
  <svg width="220" height="200" viewBox="0 0 220 200" fill="none">
    {/* Open book */}
    <path d="M110 60 L110 170" stroke={c} strokeWidth="2" />
    <path d="M110 60 Q75 55 40 65 L40 170 Q75 160 110 170" stroke={c} strokeWidth="2.5" fill="none" />
    <path d="M110 60 Q145 55 180 65 L180 170 Q145 160 110 170" stroke={c} strokeWidth="2.5" fill="none" />
    {/* Text lines in book */}
    {[85, 100, 115, 130].map(y => (
      <line key={y} x1="55" y1={y} x2="95" y2={y} stroke={c} strokeWidth="1.5" opacity=".4" />
    ))}
    {[85, 100, 115, 130].map(y => (
      <line key={y + 200} x1="125" y1={y} x2="165" y2={y} stroke={c} strokeWidth="1.5" opacity=".4" />
    ))}
    {/* Graduation cap */}
    <polygon points="110,18 150,34 110,50 70,34" stroke={c} strokeWidth="2.5" fill="none" />
    <rect x="105" y="34" width="10" height="22" rx="2" fill={c} opacity=".4" />
    <line x1="150" y1="34" x2="150" y2="52" stroke={c} strokeWidth="2" />
    <circle cx="150" cy="55" r="5" fill={c} opacity=".6" />
    {/* Sports ball */}
    <circle cx="185" cy="155" r="28" stroke={c} strokeWidth="2.5" />
    <path d="M160 143 Q185 135 210 143" stroke={c} strokeWidth="1.5" fill="none" opacity=".5" />
    <path d="M158 155 Q185 148 212 155" stroke={c} strokeWidth="1.5" fill="none" opacity=".5" />
    <path d="M160 167 Q185 175 210 167" stroke={c} strokeWidth="1.5" fill="none" opacity=".5" />
    {/* Stars */}
    <circle cx="30" cy="30" r="3" fill={c} opacity=".4" />
    <circle cx="20" cy="48" r="2" fill={c} opacity=".3" />
  </svg>
);

const HospitalitySVG = ({ c }) => (
  <svg width="220" height="200" viewBox="0 0 220 200" fill="none">
    {/* Venue arch entrance */}
    <path d="M60 185 L60 100 Q110 55 160 100 L160 185" stroke={c} strokeWidth="2.5" fill="none" />
    <line x1="45" y1="185" x2="175" y2="185" stroke={c} strokeWidth="2.5" strokeLinecap="round" />
    {/* Pillars */}
    <rect x="56" y="130" width="12" height="55" rx="2" stroke={c} strokeWidth="1.5" opacity=".5" />
    <rect x="152" y="130" width="12" height="55" rx="2" stroke={c} strokeWidth="1.5" opacity=".5" />
    {/* Door */}
    <path d="M94 185 L94 148 Q110 136 126 148 L126 185" stroke={c} strokeWidth="2" fill="none" />
    {/* Round table */}
    <ellipse cx="110" cy="135" rx="26" ry="10" stroke={c} strokeWidth="2" opacity=".5" />
    <line x1="110" y1="145" x2="110" y2="162" stroke={c} strokeWidth="2" opacity=".4" />
    {/* Chairs around table */}
    <ellipse cx="85" cy="130" rx="10" ry="6" stroke={c} strokeWidth="1.5" opacity=".4" />
    <ellipse cx="135" cy="130" rx="10" ry="6" stroke={c} strokeWidth="1.5" opacity=".4" />
    {/* Stars / event sparkle */}
    <circle cx="30" cy="50" r="3.5" fill={c} opacity=".6" />
    <circle cx="46" cy="34" r="2.5" fill={c} opacity=".45" />
    <circle cx="20" cy="32" r="2" fill={c} opacity=".35" />
    <circle cx="190" cy="50" r="3.5" fill={c} opacity=".6" />
    <circle cx="200" cy="34" r="2.5" fill={c} opacity=".45" />
    <circle cx="175" cy="36" r="2" fill={c} opacity=".35" />
    {/* Decorative top element */}
    <line x1="90" y1="68" x2="130" y2="68" stroke={c} strokeWidth="1.5" opacity=".4" />
    <circle cx="110" cy="62" r="5" stroke={c} strokeWidth="2" opacity=".5" />
  </svg>
);

const ILLUSTRATIONS = {
  healthcare: HealthcareSVG,
  'home-maintenance': HomeMaintenanceSVG,
  'home-improvements': HomeImprovementsSVG,
  'building-services': BuildingServicesSVG,
  'facility-services': FacilityServicesSVG,
  'personal-care': PersonalCareSVG,
  'auto-services': AutoServicesSVG,
  'education-services': EducationServicesSVG,
  hospitality: HospitalitySVG,
};

const ICON_MAP = {
  Calendar, Users, BarChart3, MessageSquare, FileText, TrendingUp, Zap, Shield,
  Wrench, ShieldCheck, CarFront, Package, Boxes, BellRing, Landmark, Bot,
};

const SITE = 'https://www.ezeitpro.com';

// ── Page Component ────────────────────────────────────────────────────────────

export default function UseCasePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useWebsiteTheme();
  const [isMobile, setIsMobile] = useState(false);

  const data = USE_CASES.find(uc => uc.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);


  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (!data) {
    navigate('/');
    return null;
  }

  const d = isDark ? {
    bg: NAVY.bg, bgAlt: NAVY.bgAlt, surface: NAVY.surface,
    text: NAVY.text, textSec: NAVY.textSec, textMuted: NAVY.textMuted,
    border: NAVY.border,
    primary: '#E91E63', primaryCont: 'rgba(233,30,99,0.15)',
    badgeText: '#F48FB1',
    footBg: NAVY.footBg,
    greenBg: 'rgba(76,175,80,0.12)', greenText: '#81C784', greenBorder: 'rgba(76,175,80,0.3)',
    heroBg: 'linear-gradient(135deg,#1a0a2e 0%,#16213e 100%)',
    sh: '0 2px 12px rgba(3,8,20,0.5)',
    sh2: '0 4px 24px rgba(3,8,20,0.4)',
    chipBg: 'rgba(233,30,99,0.2)', chipText: '#F48FB1',
  } : {
    bg: '#FAFAFA', bgAlt: '#F5F5F5', surface: '#FFFFFF',
    text: '#212121', textSec: '#424242', textMuted: '#757575',
    border: 'rgba(0,0,0,0.08)',
    primary: '#C2185B', primaryCont: '#FCE4EC',
    badgeText: '#C2185B',
    footBg: '#212121',
    greenBg: '#E8F5E9', greenText: '#2E7D32', greenBorder: '#C8E6C9',
    heroBg: 'linear-gradient(135deg,#FCE4EC 0%,#f5f5f5 100%)',
    sh: '0 2px 12px rgba(0,0,0,0.07)',
    sh2: '0 4px 24px rgba(0,0,0,0.09)',
    chipBg: '#FCE4EC', chipText: '#C2185B',
  };

  const Illustration = ILLUSTRATIONS[slug];
  const w = websiteTokens(isDark); // Home-shaped tokens for the shared showcase components
  const goContact = () => navigate({ pathname: '/', hash: '#contact' });

  const pageTitle = data.seoTitle || `${data.tagline} Software | EzeitPro`;
  const pageDescription = data.seoDescription || data.heroSubtitle;
  const pageUrl = `${SITE}/use-cases/${slug}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    description: pageDescription,
    url: pageUrl,
    about: {
      '@type': 'SoftwareApplication',
      name: 'EzeitPro',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: `EzeitPro for ${data.industry}: ${data.tagline}.`,
    },
  };

  const heroIllustration = Illustration && (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', background: d.primaryCont, borderRadius: '24px', padding: isMobile ? '28px' : '36px', flexShrink: 0 }}>
      <Illustration c={d.primary} />
    </div>
  );

  return (
    <div style={{ fontFamily: "'Poppins','Segoe UI',sans-serif", background: d.bg, color: d.text, minHeight: '100vh', overflowX: 'hidden' }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="EzeitPro" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={`${SITE}/image/website/og-home.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Header */}
      <header style={{ background: d.surface, borderBottom: `1px solid ${d.border}`, padding: `0 ${isMobile ? '16px' : '32px'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px', position: 'sticky', top: 0, zIndex: 100, boxShadow: d.sh }}>
        <BrandLogo height={36} tone={isDark ? 'dark' : 'light'} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ThemeToggle isDark={isDark} onToggle={toggleTheme} color={d.text} border={d.border} size={36} />
          <button
            onClick={() => navigate(-1)}
            style={{ background: d.bgAlt, border: `1px solid ${d.border}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: d.textSec, fontWeight: 500 }}
          >
            ← Back
          </button>
          <button
            onClick={() => navigate('/')}
            style={{ background: d.primary, border: 'none', borderRadius: '8px', padding: '8px 18px', fontSize: '13px', cursor: 'pointer', color: '#fff', fontWeight: 600 }}
          >
            Home
          </button>
        </div>
      </header>

      {/* Hero */}
      <section style={{ background: d.heroBg, padding: isMobile ? '52px 20px 48px' : '72px 32px 64px', borderBottom: `1px solid ${d.border}` }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto', gap: isMobile ? '36px' : '60px', alignItems: 'center' }}>
          <div>
            <div style={{ display: 'inline-block', background: d.primary, color: '#fff', fontSize: '11px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {data.industry}
            </div>
            <h1 style={{ fontSize: isMobile ? '28px' : 'clamp(28px,3.5vw,44px)', fontWeight: 800, color: d.text, margin: '0 0 14px', lineHeight: 1.15 }}>
              EzeitPro for<br />{data.industry}
            </h1>
            <p style={{ fontSize: isMobile ? '14px' : '16px', color: d.textSec, lineHeight: 1.8, marginBottom: '24px', maxWidth: '560px' }}>
              {data.heroSubtitle}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '28px' }}>
              {data.services.map(s => (
                <span key={s} style={{ background: d.chipBg, color: d.chipText, padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 }}>{s}</span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <a
                href="/login"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: d.primary, color: '#fff', padding: '12px 26px', borderRadius: '28px', textDecoration: 'none', fontWeight: 700, fontSize: '14px' }}
              >
                Start Free Trial <ArrowRight size={15} />
              </a>
              <button
                onClick={() => navigate({ pathname: '/', hash: '#contact' })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent', color: d.primary, border: `2px solid ${d.primary}`, padding: '10px 24px', borderRadius: '28px', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}
              >
                Book a Demo
              </button>
            </div>
          </div>
          {data.heroShot ? (
            <div style={{ width: isMobile ? '100%' : '520px', maxWidth: '100%' }}>
              <DeviceCollage d={w} {...data.heroShot} eager fallback={heroIllustration} />
            </div>
          ) : heroIllustration}
        </div>
      </section>

      {/* Challenge */}
      <section style={{ padding: isMobile ? '52px 20px' : '72px 32px', background: d.bg }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <div style={{ display: 'inline-block', background: d.chipBg, color: d.chipText, fontSize: '11px', fontWeight: 700, padding: '4px 12px', borderRadius: '20px', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            The Challenge
          </div>
          <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: d.text, marginBottom: '20px' }}>
            Why {data.industry} Businesses Need a Better Solution
          </h2>
          <div style={{ background: d.surface, border: `1px solid ${d.border}`, borderLeft: `4px solid ${d.primary}`, borderRadius: '12px', padding: isMobile ? '20px' : '28px 32px', boxShadow: d.sh }}>
            <p style={{ fontSize: isMobile ? '14px' : '15px', color: d.textSec, lineHeight: 1.9, margin: 0 }}>
              {data.challenge}
            </p>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section style={{ padding: isMobile ? '52px 20px' : '72px 32px', background: d.bgAlt }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div style={{ display: 'inline-block', background: d.primary, color: '#fff', fontSize: '11px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              How EzeitPro Helps
            </div>
            <h2 style={{ fontSize: isMobile ? '22px' : '30px', fontWeight: 700, color: d.text, margin: 0 }}>
              Everything You Need, Built for {data.industry}
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '20px' }}>
            {data.solutions.map((sol, i) => {
              const IconComp = ICON_MAP[sol.icon] || Calendar;
              return (
                <div
                  key={i}
                  style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '14px', padding: isMobile ? '20px' : '24px', boxShadow: d.sh, transition: 'all 0.2s', ...(i === 4 && data.solutions.length === 5 && !isMobile ? { gridColumn: 'span 3', display: 'flex', gap: '24px', alignItems: 'flex-start' } : {}) }}
                >
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: d.primaryCont, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: i === 4 && data.solutions.length === 5 && !isMobile ? '0' : '14px', flexShrink: 0, ...(i === 4 && data.solutions.length === 5 && !isMobile ? { marginRight: '0' } : {}) }}>
                    <IconComp size={22} color={d.primary} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: d.text, marginBottom: '8px', marginTop: 0 }}>{sol.title}</h3>
                    <p style={{ fontSize: '13px', color: d.textSec, lineHeight: 1.8, margin: 0 }}>{sol.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Screenshot showcase (industries that provide `showcase`) */}
      {data.showcase && (
        <section style={{ padding: isMobile ? '52px 20px' : '88px 32px', background: d.bg }}>
          <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '64px' }}>
              <div style={{ display: 'inline-block', background: d.primary, color: '#fff', fontSize: '11px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                See It In Action
              </div>
              <h2 style={{ fontSize: isMobile ? '24px' : '34px', fontWeight: 700, color: d.text, margin: '0 0 12px' }}>
                A Closer Look at the Platform
              </h2>
              <p style={{ fontSize: '15px', color: d.textSec, lineHeight: 1.8, maxWidth: '620px', margin: '0 auto' }}>
                Real screens from the workshop, stores and back office. Sample data shown.
              </p>
            </div>
            <ModuleShowcase d={w} isDark={isDark} isMobile={isMobile} modules={data.showcase} onContact={goContact} ctaLabel="Contact us about this" />
          </div>
        </section>
      )}

      {/* Capability tiles */}
      {data.highlights && (
        <section style={{ padding: isMobile ? '52px 20px' : '72px 32px', background: d.bgAlt }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: d.text, margin: '0 0 10px' }}>
                Everything a Service Centre Needs
              </h2>
              <p style={{ fontSize: '14px', color: d.textMuted, margin: 0 }}>Dealership, finance, inventory and AI modules are part of the Enterprise plan.</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px' }}>
              {data.highlights.map((h) => {
                const HIcon = ICON_MAP[h.icon] || Zap;
                return (
                  <div key={h.title} style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '14px', padding: '20px 22px', boxShadow: d.sh }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                      <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: d.primaryCont, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <HIcon size={19} color={d.primary} />
                      </div>
                      <h3 style={{ fontSize: '14.5px', fontWeight: 700, color: d.text, margin: 0 }}>{h.title}</h3>
                    </div>
                    <p style={{ fontSize: '13px', color: d.textSec, lineHeight: 1.7, margin: 0 }}>{h.body}</p>
                  </div>
                );
              })}
            </div>
            <div style={{ textAlign: 'center', marginTop: '36px' }}>
              <EnterpriseBadge d={w} />
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section style={{ padding: isMobile ? '52px 20px' : '72px 32px', background: d.bg }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ display: 'inline-block', background: d.greenBg, color: d.greenText, fontSize: '11px', fontWeight: 700, padding: '4px 12px', borderRadius: '20px', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.08em', border: `1px solid ${d.greenBorder}` }}>
              Key Benefits
            </div>
            <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: d.text, margin: 0 }}>
              What Changes When You Use EzeitPro
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
            {data.benefits.map((b, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', background: d.surface, border: `1px solid ${d.border}`, borderRadius: '12px', padding: '16px 18px', boxShadow: d.sh }}>
                <CheckCircle size={18} color={d.greenText} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span style={{ fontSize: '14px', color: d.textSec, lineHeight: 1.65 }}>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UtilStop Marketplace */}
      <section style={{ padding: isMobile ? '48px 20px' : '64px 32px', background: d.bgAlt }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <div style={{ background: isDark ? 'linear-gradient(135deg,#1a0a2e,#16213e)' : `linear-gradient(135deg,${d.primaryCont},#f5f5f5)`, border: `1px solid ${d.border}`, borderRadius: '16px', padding: isMobile ? '28px 20px' : '40px 40px', textAlign: 'center', boxShadow: d.sh2 }}>
            <div style={{ display: 'inline-block', background: d.primary, color: '#fff', fontSize: '11px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              UtilStop Marketplace
            </div>
            <h2 style={{ fontSize: isMobile ? '20px' : '26px', fontWeight: 700, color: d.text, margin: '0 0 12px' }}>
              Grow Beyond Your Existing Customer Base
            </h2>
            <p style={{ fontSize: '15px', color: d.textSec, lineHeight: 1.8, margin: '0 auto 24px', maxWidth: '600px' }}>
              EzeitPro connects your {data.industry.toLowerCase()} business to UtilStop Marketplace — a dedicated service discovery platform where high-intent customers search, compare, and book local services. Get listed once and start receiving new bookings automatically, at no additional cost.
            </p>
            <p style={{ fontSize: '13px', color: d.textMuted, marginBottom: '20px' }}>
              🌏 &nbsp;Currently available in <strong style={{ color: d.textSec }}>India</strong> and <strong style={{ color: d.textSec }}>UAE</strong> &nbsp;·&nbsp; More countries coming soon
            </p>
            <a
              href="https://www.utilstop.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: d.primary, color: '#fff', padding: '12px 28px', borderRadius: '28px', textDecoration: 'none', fontWeight: 700, fontSize: '14px' }}
            >
              Explore UtilStop Marketplace <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ padding: isMobile ? '52px 20px' : '80px 32px', background: isDark ? 'linear-gradient(135deg,#1a0a2e,#16213e)' : `linear-gradient(135deg,${d.primaryCont},#ede7f6)`, textAlign: 'center' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{ fontSize: isMobile ? '24px' : '34px', fontWeight: 800, color: d.text, marginBottom: '12px' }}>
            Ready to Transform Your {data.industry} Business?
          </h2>
          <p style={{ fontSize: '15px', color: d.textSec, lineHeight: 1.8, marginBottom: '32px' }}>
            Join hundreds of service businesses using EzeitPro to operate smarter, grow faster, and delight their customers every day.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <a
              href="/login"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: d.primary, color: '#fff', padding: '14px 32px', borderRadius: '28px', textDecoration: 'none', fontWeight: 700, fontSize: '15px', boxShadow: `0 4px 16px ${isDark ? 'rgba(233,30,99,0.4)' : 'rgba(194,24,91,0.3)'}` }}
            >
              Start Free Trial <ArrowRight size={16} />
            </a>
            <button
                onClick={() => navigate({ pathname: '/', hash: '#contact' })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent', color: d.primary, border: `2px solid ${d.primary}`, padding: '10px 24px', borderRadius: '28px', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}
              >
                Book a Demo
              </button>
          </div>
        </div>
      </section>

      {/* Browse other use cases */}
      <section style={{ padding: isMobile ? '40px 20px' : '56px 32px', background: d.bg, borderTop: `1px solid ${d.border}` }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: d.text, marginBottom: '20px', textAlign: 'center' }}>
            Explore Other Industries
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
            {USE_CASES.filter(uc => uc.slug !== slug).map(uc => (
              <Link
                key={uc.slug}
                to={`/use-cases/${uc.slug}`}
                style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '24px', padding: '7px 18px', fontSize: '13px', color: d.textSec, textDecoration: 'none', fontWeight: 500, transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = d.primaryCont; e.currentTarget.style.color = d.primary; }}
                onMouseLeave={e => { e.currentTarget.style.background = d.surface; e.currentTarget.style.color = d.textSec; }}
              >
                {uc.industry}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: d.footBg, color: '#9E9E9E', padding: '24px 32px', textAlign: 'center', fontSize: '13px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '16px' }}>
          {[
            { href: 'https://www.instagram.com/ezeit_pro/', icon: BsInstagram, label: 'Instagram' },
            { href: 'https://www.facebook.com/profile.php?id=61589841952735', icon: BsFacebook, label: 'Facebook' },
            { href: 'https://www.linkedin.com/company/ezeitpro/', icon: BsLinkedin, label: 'LinkedIn' },
            { href: 'https://www.youtube.com/@EzeitPro', icon: BsYoutube, label: 'YouTube' },
            { href: 'https://x.com/ezeitpro', icon: BsTwitterX, label: 'X' },
            { href: 'https://wa.me/971561303601', icon: BsWhatsapp, label: 'WhatsApp' },
          ].map(({ href, icon: Icon, label }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              style={{ color: '#9E9E9E', textDecoration: 'none', display: 'inline-flex', transition: 'color 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.color = label === 'WhatsApp' ? '#25D366' : d.primary; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#9E9E9E'; }}
            >
              <Icon size={20} />
            </a>
          ))}
        </div>
        <p style={{ margin: 0 }}>
          © 2026 UtilStop LLC-FZ, Dubai. All rights reserved. &nbsp;·&nbsp;
          <Link to="/privacy-policy" style={{ color: '#9E9E9E', textDecoration: 'none' }}>Privacy</Link> &nbsp;·&nbsp;
          <Link to="/terms" style={{ color: '#9E9E9E', textDecoration: 'none' }}>Terms</Link> &nbsp;·&nbsp;
          <Link to="/security" style={{ color: '#9E9E9E', textDecoration: 'none' }}>Security</Link>
        </p>
      </footer>

    </div>
  );
}
